/**
 * Medidor de força de senha do primeiro acesso e da redefinição.
 *
 * Ele NÃO é a regra de segurança — quem recusa uma senha fraca de verdade é a API, que é
 * o único lado que o navegador não consegue contornar. O que este arquivo faz é impedir
 * que a pessoa descubra o problema só depois de enviar, e dizer em uma frase o que falta.
 *
 * **O medidor aconselha; só o comprimento bloqueia** (spec 3.5, 28/09/2026). A API recusa
 * apenas senha curta e senha de lista pública de vazadas (NIST SP 800-63B). Antes esta tela
 * bloqueava por regra de composição e liberava com 10 caracteres enquanto a API exigia 12 —
 * a pessoa via "Boa senha", enviava, e recebia "previsível". O mínimo abaixo tem de ser o
 * `TAMANHO_MINIMO_DA_SENHA` da API; um teste da API falha se os dois divergirem.
 */

import { semAcento } from "./texto";

/** Comprimento mínimo — o MESMO da API. Abaixo disto o botão de enviar fica desligado. */
export const MINIMO_DE_CARACTERES = 8;

export type NivelDeSenha = "curta" | "fraca" | "razoavel" | "boa" | "forte";

export interface ForcaDaSenha {
  nivel: NivelDeSenha;
  /** 0 a 4 — quantos segmentos do medidor acendem. */
  pontos: number;
  /** Se a tela deixa enviar. Depende SÓ do comprimento — o resto é conselho. */
  aceitavel: boolean;
  /** O que falta, em uma frase só. */
  dica: string;
}

/* Lista curta e proposital: são os padrões que aparecem em senha de primeiro acesso de
   sistema corporativo. Não é substituto de uma lista de senhas vazadas — essa vive na API. */
const PADROES_OBVIOS = [
  "senha",
  "password",
  "geovendas",
  "portal",
  "123456",
  "qwerty",
  "abc123",
  "admin",
  "mudar",
  "trocar",
  "implanta",
] as const;

const TEM_MINUSCULA = /\p{Ll}/u;
const TEM_MAIUSCULA = /\p{Lu}/u;
const TEM_DIGITO = /\p{Nd}/u;
const TEM_SIMBOLO = /[^\p{L}\p{N}]/u;

/** Trechos do próprio contexto da pessoa que não podem virar senha (e-mail, empresa). */
export function fragmentosAEvitar(...textos: Array<string | null | undefined>): string[] {
  const fragmentos = new Set<string>();
  for (const texto of textos) {
    if (!texto) continue;
    // O e-mail entra inteiro e também só a parte antes do @, que é o palpite mais comum.
    for (const pedaco of semAcento(texto).toLowerCase().split(/[^a-z0-9]+/)) {
      if (pedaco.length >= 4) fragmentos.add(pedaco);
    }
  }
  return [...fragmentos];
}

/** Três ou mais caracteres em sequência (`abc`, `321`) ou repetidos (`aaa`). */
function temCorridaTrivial(senha: string): boolean {
  for (let i = 0; i + 2 < senha.length; i += 1) {
    const a = senha.charCodeAt(i);
    const b = senha.charCodeAt(i + 1);
    const c = senha.charCodeAt(i + 2);
    const passo = b - a;
    if ((passo === 0 || passo === 1 || passo === -1) && c - b === passo) return true;
  }
  return false;
}

function variedade(senha: string): number {
  return [TEM_MINUSCULA, TEM_MAIUSCULA, TEM_DIGITO, TEM_SIMBOLO].filter((teste) =>
    teste.test(senha),
  ).length;
}

export function medirSenha(senha: string, evitar: readonly string[] = []): ForcaDaSenha {
  if (senha.length === 0) {
    return {
      nivel: "curta",
      pontos: 0,
      aceitavel: false,
      dica: `Use pelo menos ${MINIMO_DE_CARACTERES} caracteres.`,
    };
  }

  if (senha.length < MINIMO_DE_CARACTERES) {
    const faltam = MINIMO_DE_CARACTERES - senha.length;
    return {
      nivel: "curta",
      pontos: 0,
      aceitavel: false,
      dica: `Faltam ${faltam} ${faltam === 1 ? "caractere" : "caracteres"} para o mínimo de ${MINIMO_DE_CARACTERES}.`,
    };
  }

  const normalizada = semAcento(senha).toLowerCase();
  const ecoaContexto = evitar.some((fragmento) => normalizada.includes(fragmento));
  const padraoObvio = PADROES_OBVIOS.some((padrao) => normalizada.includes(padrao));

  if (ecoaContexto || padraoObvio) {
    return {
      nivel: "fraca",
      pontos: 1,
      aceitavel: true,
      dica: ecoaContexto
        ? "Essa senha repete o seu e-mail ou o nome da empresa — é o primeiro palpite de quem tenta entrar no seu lugar."
        : "Essa senha contém um padrão conhecido. Troque por algo que só você saiba.",
    };
  }

  const tipos = variedade(senha);
  let pontos = 1;
  if (senha.length >= 12) pontos += 1;
  if (senha.length >= 16) pontos += 1;
  if (tipos >= 3) pontos += 1;
  if (tipos >= 4) pontos += 1;
  if (temCorridaTrivial(senha)) pontos -= 1;
  pontos = Math.min(4, Math.max(1, pontos));

  if (pontos <= 1) {
    return {
      nivel: "fraca",
      pontos,
      aceitavel: true,
      dica: "Misture maiúsculas, números e símbolos — ou use uma frase mais longa, que vale ainda mais.",
    };
  }

  if (pontos === 2) {
    return {
      nivel: "razoavel",
      pontos,
      aceitavel: true,
      dica: "Serve. Cada caractere a mais protege mais do que qualquer regra de composição.",
    };
  }

  return {
    nivel: pontos === 3 ? "boa" : "forte",
    pontos,
    aceitavel: true,
    dica: pontos === 3 ? "Boa senha." : "Senha forte.",
  };
}

export const ROTULO_DO_NIVEL: Record<NivelDeSenha, string> = {
  curta: "curta demais",
  fraca: "fraca",
  razoavel: "razoável",
  boa: "boa",
  forte: "forte",
};
